import React, { useState, useEffect } from 'react';
import { getMyShop, updateMyShop, createListing, withdrawListing } from '../../services/marketplaceService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { resolveImageUrl, onImageError, ART_PLACEHOLDER_SVG } from '../../utils/imageUrl';

const PLACEHOLDER = ART_PLACEHOLDER_SVG;

export default function MyShop() {
  const [shop, setShop] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({ shop_name: '', bio: '', policies: '' });
  const [listingForm, setListingForm] = useState({ art_id: '', order_type: 'sale', price: '' });
  const [showListingForm, setShowListingForm] = useState(false);

  const fetchShop = async () => {
    try {
      const res = await getMyShop();
      const data = res?.data?.data;
      setShop(data?.shop || null);
      setListings(data?.listings || []);
      if (data?.shop) {
        setForm({
          shop_name: data.shop.shop_name || '',
          bio: data.shop.bio || '',
          policies: data.shop.policies || '',
        });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to load shop.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchShop(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await updateMyShop(form);
      setMessage({ type: 'success', text: 'Shop updated!' });
      setEditMode(false);
      fetchShop();
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await createListing({
        art_id: listingForm.art_id.trim(),
        order_type: listingForm.order_type,
        price: parseFloat(listingForm.price),
      });
      setMessage({ type: 'success', text: 'Art listed on marketplace!' });
      setShowListingForm(false);
      setListingForm({ art_id: '', order_type: 'sale', price: '' });
      fetchShop();
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to list art.' });
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async (artId) => {
    if (!window.confirm('Withdraw this artwork from the marketplace?')) return;
    try {
      await withdrawListing(artId);
      setMessage({ type: 'success', text: 'Art withdrawn from marketplace.' });
      fetchShop();
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to withdraw.' });
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.65rem',
    borderRadius: '0.5rem',
    border: '1px solid #333',
    background: '#0f0f23',
    color: '#fff',
    fontSize: '0.95rem',
  };

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, margin: 0 }}>🏪 My Shop</h1>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => setShowListingForm(!showListingForm)}
              style={{
                padding: '0.5rem 1rem', borderRadius: '0.5rem', border: 'none',
                background: '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer',
              }}
            >
              + List Art
            </button>
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                style={{
                  padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #7c3aed',
                  background: 'transparent', color: '#7c3aed', fontWeight: 600, cursor: 'pointer',
                }}
              >
                Edit Shop
              </button>
            )}
          </div>
        </div>

        {message && (
          <div style={{
            padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem',
            background: message.type === 'error' ? '#ef444433' : '#10b98133',
            color: message.type === 'error' ? '#ef4444' : '#10b981',
          }}>
            {message.text}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>Loading...</div>
        ) : (
          <>
            {/* Shop Settings */}
            {editMode ? (
              <form onSubmit={handleSave} style={{ background: '#1a1a2e', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0 }}>Shop Settings</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.85rem' }}>Shop Name</label>
                    <input value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} style={inputStyle} placeholder="My Art Studio" />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.85rem' }}>Bio</label>
                    <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="About your art..." />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.85rem' }}>Policies</label>
                    <textarea value={form.policies} onChange={(e) => setForm({ ...form, policies: e.target.value })} style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Shipping, returns..." />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <button type="submit" disabled={saving} style={{ padding: '0.6rem 1.2rem', borderRadius: '0.5rem', border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button type="button" onClick={() => setEditMode(false)} style={{ padding: '0.6rem 1.2rem', borderRadius: '0.5rem', border: '1px solid #555', background: 'transparent', color: '#888', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </form>
            ) : shop && (
              <div style={{ background: '#1a1a2e', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: '0 0 0.5rem' }}>{shop.shop_name || 'Untitled Shop'}</h2>
                <p style={{ color: '#aaa', margin: 0 }}>{shop.bio || 'No bio set.'}</p>
                <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.5rem' }}>Status: {shop.status}</p>
              </div>
            )}

            {/* New Listing Form */}
            {showListingForm && (
              <form onSubmit={handleCreateListing} style={{ background: '#1a1a2e', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0 }}>List Artwork on Marketplace</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.85rem' }}>Art ID</label>
                    <input value={listingForm.art_id} onChange={(e) => setListingForm({ ...listingForm, art_id: e.target.value })} required style={inputStyle} placeholder="Paste art_id" />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.3rem', fontWeight: 600, fontSize: '0.85rem' }}>Price ($)</label>
                    <input type="number" step="0.01" min="0.01" value={listingForm.price} onChange={(e) => setListingForm({ ...listingForm, price: e.target.value })} required style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                  <button type="submit" disabled={saving} style={{ padding: '0.6rem 1.2rem', borderRadius: '0.5rem', border: 'none', background: '#10b981', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>
                    {saving ? 'Listing...' : 'List Art'}
                  </button>
                  <button type="button" onClick={() => setShowListingForm(false)} style={{ padding: '0.6rem 1.2rem', borderRadius: '0.5rem', border: '1px solid #555', background: 'transparent', color: '#888', cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Current Listings */}
            <h2 style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Active Listings ({listings.length})</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
              {listings.length === 0 && <p style={{ color: '#888' }}>No artworks listed yet. Click "+ List Art" to get started.</p>}
              {listings.map((art) => (
                <div key={art.art_id} style={{ background: '#1a1a2e', borderRadius: '0.75rem', overflow: 'hidden' }}>
                  <img
                    src={resolveImageUrl(art.image_url)}
                    alt={art.title}
                    style={{ width: '100%', height: 160, objectFit: 'cover' }}
                    onError={onImageError}
                  />
                  <div style={{ padding: '0.75rem' }}>
                    <h4 style={{ margin: '0 0 0.3rem', fontSize: '0.95rem' }}>{art.title || 'Untitled'}</h4>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '1rem',
                        background: '#10b98133',
                        color: '#10b981',
                      }}>
                        Sale
                      </span>
                      <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.9rem' }}>
                        ${art.price?.toFixed(2)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleWithdraw(art.art_id)}
                      style={{
                        width: '100%', marginTop: '0.5rem', padding: '0.4rem',
                        borderRadius: '0.3rem', border: '1px solid #ef444466',
                        background: 'transparent', color: '#ef4444', fontSize: '0.8rem',
                        cursor: 'pointer',
                      }}
                    >
                      Withdraw
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
